// Reads the DocumentEditor's laid-out document for the Outline pane (headings),
// the status bar's word / character counts and the Suggestions pane
// (tracked revisions). Syncfusion has no public headings or word-count API,
// so the walk goes over its layout model (pages → body widgets → paragraphs /
// tables → lines → elements) through loose, defensive views — a missing
// field just yields nothing. Revisions use the public `revisions` API.

import { useCallback, useEffect, useRef, useState } from "react";
import type { DocumentEditorContainerComponent } from "@syncfusion/ej2-react-documenteditor";

export type DocEditor = NonNullable<DocumentEditorContainerComponent["documentEditor"]>;
export type DocRevision = ReturnType<DocEditor["revisions"]["get"]>;

export interface OutlineItem {
    key: string;
    level: number;
    text: string;
    /** Section and block index — the first two parts of the hierarchical offset. */
    section: number;
    block: number;
    paragraph: unknown;
}

export interface DocStats {
    words: number;
    characters: number;
    charactersNoSpaces: number;
}

// ── Loose views of the layout model ─────────────────────────────────────────

interface LooseElement {
    text?: unknown;
    listLevel?: unknown;
    commentId?: unknown;
    footnoteType?: unknown;
    fieldType?: unknown;
}
interface LooseLine { children?: LooseElement[] }
interface LooseStyle { name?: unknown; basedOn?: unknown }
interface LooseWidget {
    childWidgets?: unknown[];
    paragraphFormat?: { outlineLevel?: unknown; baseStyle?: LooseStyle };
    tableFormat?: unknown;
    previousSplitWidget?: unknown;
}
interface LoosePage { bodyWidgets?: LooseWidget[] }

// FieldElementBox.fieldType: 0 = begin, 1 = end, 2 = separator. Text between
// begin and separator is the field code ("TOC \o …"), not document text.
const FIELD_BEGIN = 0;
const FIELD_END = 1;
const FIELD_SEPARATOR = 2;

function isParagraph(w: LooseWidget): boolean {
    return w.paragraphFormat !== undefined && w.tableFormat === undefined;
}

/** Visible text of a paragraph part; `fields` tracks fields that span paragraphs. */
function paragraphText(p: LooseWidget, fields: boolean[]): string {
    let out = "";
    for (const line of (p.childWidgets ?? []) as LooseLine[]) {
        for (const el of line?.children ?? []) {
            if (typeof el.fieldType === "number") {
                if (el.fieldType === FIELD_BEGIN) fields.push(true);
                else if (el.fieldType === FIELD_SEPARATOR) { if (fields.length > 0) fields[fields.length - 1] = false; }
                else if (el.fieldType === FIELD_END) fields.pop();
                continue;
            }
            if (typeof el.text !== "string") continue;
            // List numbers / bullets, comment marks and footnote references aren't text.
            if (el.listLevel !== undefined || el.commentId !== undefined || el.footnoteType !== undefined) continue;
            if (fields.includes(true)) continue;
            out += el.text;
        }
    }
    return out;
}

function headingLevel(p: LooseWidget): number | null {
    const outline = p.paragraphFormat?.outlineLevel;
    if (typeof outline === "string") {
        const m = /^Level([1-9])$/.exec(outline);
        if (m) return Number(m[1]);
    }
    // Styles based on a heading ("My heading" → Heading 2) count too.
    let style: LooseStyle | undefined = p.paragraphFormat?.baseStyle;
    for (let depth = 0; style && depth < 5; depth++) {
        const m = /^heading\s*([1-9])$/i.exec(String(style.name ?? ""));
        if (m) return Number(m[1]);
        style = style.basedOn as LooseStyle | undefined;
    }
    return null;
}

export function countWords(text: string): number {
    return text.match(/\S+/g)?.length ?? 0;
}

function parseOffset(offset: string | undefined | null): [number, number] | null {
    const m = /^(\d+);(\d+)/.exec(offset ?? "");
    return m ? [Number(m[1]), Number(m[2])] : null;
}

export function analyzeDocument(ed: DocEditor): { outline: OutlineItem[]; stats: DocStats } {
    const pages = (ed.documentHelper as unknown as { pages?: LoosePage[] }).pages ?? [];
    const fields: boolean[] = [];
    const outline: OutlineItem[] = [];
    let words = 0;
    let characters = 0;
    let charactersNoSpaces = 0;

    const visit = (w: LooseWidget, topLevel: boolean) => {
        if (!w) return;
        if (isParagraph(w)) {
            const text = paragraphText(w, fields);
            words += countWords(text);
            characters += text.length;
            charactersNoSpaces += text.replace(/\s/g, "").length;
            // A heading split over two pages is listed once (its first part).
            if (topLevel && !w.previousSplitWidget) {
                const level = headingLevel(w);
                const title = text.replace(/\s+/g, " ").trim();
                if (level !== null && title) {
                    let pos: [number, number] | null = null;
                    try {
                        pos = parseOffset(ed.selection.getHierarchicalIndex(w as never, "0"));
                    } catch {
                        pos = null;
                    }
                    outline.push({
                        key: `${outline.length}:${pos?.join(";") ?? "?"}`,
                        level,
                        text: title.length > 200 ? `${title.slice(0, 200)}…` : title,
                        section: pos?.[0] ?? -1,
                        block: pos?.[1] ?? -1,
                        paragraph: w
                    });
                }
            }
            return;
        }
        // Table → rows → cells → blocks.
        for (const child of (w.childWidgets ?? []) as LooseWidget[]) visit(child, false);
    };

    for (const page of pages) {
        for (const body of page?.bodyWidgets ?? []) {
            for (const block of (body?.childWidgets ?? []) as LooseWidget[]) visit(block, true);
        }
    }
    return { outline, stats: { words, characters, charactersNoSpaces } };
}

export function readRevisions(ed: DocEditor): DocRevision[] {
    const all = ed.revisions;
    const out: DocRevision[] = [];
    for (let i = 0; i < all.length; i++) {
        const rev = all.get(i);
        if (rev) out.push(rev);
    }
    return out;
}

/** Moves the caret to the start of a heading and scrolls it into view. */
export function navigateToHeading(ed: DocEditor, item: OutlineItem): boolean {
    try {
        if (item.section >= 0 && item.block >= 0) {
            const at = `${item.section};${item.block};0`;
            ed.selection.select(at, at);
        } else {
            ed.selection.selectParagraphInternal(item.paragraph as never, true);
        }
        ed.focusIn();
        return true;
    } catch {
        return false;
    }
}

const EMPTY_STATS: DocStats = { words: 0, characters: 0, charactersNoSpaces: 0 };

/**
 * Outline, counts and revisions for the open document. `schedule()` after
 * edits (debounced), `refresh()` after a load or an accept / reject,
 * `onSelection()` on every caret move (cheap: selection word count +
 * which heading the caret is under).
 */
export function useDocInsights(getEditor: () => DocEditor | null | undefined) {
    const [outline, setOutline] = useState<OutlineItem[]>([]);
    const [stats, setStats] = useState<DocStats>(EMPTY_STATS);
    const [revisions, setRevisions] = useState<DocRevision[]>([]);
    const [selectionWords, setSelectionWords] = useState<number | null>(null);
    const [caret, setCaret] = useState<[number, number] | null>(null);
    const timerRef = useRef<number | null>(null);
    const frameRef = useRef<number | null>(null);

    const refresh = useCallback(() => {
        if (timerRef.current !== null) {
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        const ed = getEditor();
        if (!ed) return;
        try {
            const result = analyzeDocument(ed);
            setOutline(result.outline);
            setStats(result.stats);
        } catch {
            /* layout mid-update — the next change refreshes */
        }
        try {
            setRevisions(readRevisions(ed));
        } catch {
            setRevisions([]);
        }
    }, [getEditor]);

    const schedule = useCallback((delayMs = 600) => {
        if (timerRef.current !== null) window.clearTimeout(timerRef.current);
        timerRef.current = window.setTimeout(() => {
            timerRef.current = null;
            refresh();
        }, delayMs);
    }, [refresh]);

    const onSelection = useCallback(() => {
        if (frameRef.current !== null) return;
        frameRef.current = window.requestAnimationFrame(() => {
            frameRef.current = null;
            const ed = getEditor();
            if (!ed) return;
            try {
                const sel = ed.selection;
                setSelectionWords(sel.isEmpty ? null : countWords(sel.text ?? ""));
                const next = parseOffset(sel.startOffset);
                // Only re-render when the caret changes paragraph.
                setCaret((prev) =>
                    (prev === null && next === null) || (prev && next && prev[0] === next[0] && prev[1] === next[1]) ? prev : next
                );
            } catch {
                /* no selection yet */
            }
        });
    }, [getEditor]);

    useEffect(() => () => {
        if (timerRef.current !== null) window.clearTimeout(timerRef.current);
        if (frameRef.current !== null) window.cancelAnimationFrame(frameRef.current);
    }, []);

    // The heading the caret sits under — the last one at or before it.
    let currentKey: string | null = null;
    if (caret) {
        for (const item of outline) {
            if (item.section < caret[0] || (item.section === caret[0] && item.block <= caret[1])) currentKey = item.key;
            else break;
        }
    }

    return { outline, stats, revisions, selectionWords, currentKey, refresh, schedule, onSelection };
}

export function readFlag(key: string, fallback = false): boolean {
    try {
        const v = window.localStorage.getItem(key);
        return v === null ? fallback : v === "1";
    } catch {
        return fallback;
    }
}

export function writeFlag(key: string, value: boolean): void {
    try {
        window.localStorage.setItem(key, value ? "1" : "0");
    } catch {
        /* private mode */
    }
}
